import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Quote, Page } from "@/lib/types";
import { api, errMsg, assetUrl } from "@/lib/api";
import { StatusBar, reportDelivery } from "@/components/builder/StatusBar";
import { LeftPanel } from "@/components/builder/LeftPanel";
import { ContentTab } from "@/components/builder/ContentTab";
import { PricingTab } from "@/components/builder/PricingTab";
import { ImagesTab } from "@/components/builder/ImagesTab";
import { FilesTab } from "@/components/builder/FilesTab";
import { HistoryTab } from "@/components/builder/HistoryTab";
import { ProposalPage } from "@/components/proposal/ProposalPage";
import { PdfPages } from "@/components/proposal/PdfPages";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, Receipt, Image as ImageIcon, Paperclip, History, Layers, Eye, PenLine } from "lucide-react";

const EDITABLE = ["draft", "internal_review"];
const SAVE_FIELDS = ["title", "pages", "line_items", "discounts", "attachments", "cover_image_file_id", "cover_original_file_id", "before_image_file_id", "mockup_source_file_id", "notes", "valid_until", "tax_rate"];

function recomputePricing(q: Quote) {
  const subtotal = +q.line_items.reduce((s, i) => s + Number(i.qty) * Number(i.unit_price), 0).toFixed(2);
  const discounts = q.discounts.map((d) => ({ ...d, amount: d.type === "percent" ? +(subtotal * Number(d.value) / 100).toFixed(2) : +Number(d.value).toFixed(2) }));
  const discount_total = Math.min(+discounts.reduce((s, d) => s + d.amount, 0).toFixed(2), subtotal);
  const taxable = subtotal - discount_total;
  const tax = +(taxable * Number(q.tax_rate || 0) / 100).toFixed(2);
  const total = +(taxable + tax).toFixed(2);
  const depType = q.deposit_type || "percent";
  const depValue = q.deposit_value ?? 20;
  const depAmount = Math.max(depType === "percent" ? +(total * Number(depValue) / 100).toFixed(2) : Math.min(Number(depValue), total), 0);
  const OLD_NOTE = "A small deposit of 20% is required to order the aluminum trim and materials to confirm your installation";
  const DEFAULT_NOTE = "A small deposit is required to order the aluminum trim and other materials to confirm your installation. The balance is due on the day of install. We accept etransfer, credit card (with 3% fee) or financing options with iFinance Canada.";
  const rawNote = q.deposit_note ?? q.pricing?.deposit?.note;
  const note = rawNote == null || rawNote.trim() === OLD_NOTE ? DEFAULT_NOTE : rawNote;
  return { subtotal, addons_total: 0, discounts, discount_total, tax_rate: Number(q.tax_rate || 0), tax, total, deposit: { type: depType, value: Number(depValue), amount: depAmount, note } };
}

export default function QuoteBuilder() {
  const { id } = useParams();
  const [quote, setQuoteState] = useState<Quote | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<string>("");
  const [tab, setTab] = useState("content");
  const [catalog, setCatalog] = useState<any[]>([]);
  const [discountCatalog, setDiscountCatalog] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [appendixTypes, setAppendixTypes] = useState<any[]>([]);

  const setQuote = (q: Quote) => { setQuoteState(q); setDirty(false); };

  useEffect(() => {
    api.get(`/quotes/${id}`).then((r) => { setQuote(r.data); setSelected(r.data.pages[0]?.id || ""); }).catch((e) => toast.error(errMsg(e)));
    api.get("/line-items").then((r) => setCatalog(r.data));
    api.get("/discounts").then((r) => setDiscountCatalog(r.data));
    api.get("/content-sections").then((r) => setSections(r.data));
    api.get("/appendix-types").then((r) => setAppendixTypes(r.data));
  }, [id]);

  const editable = !!quote && EDITABLE.includes(quote.status);

  const update = useCallback((patch: Partial<Quote>) => {
    setQuoteState((q) => { if (!q) return q; const next = { ...q, ...patch }; return { ...next, pricing: recomputePricing(next) }; });
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    if (!quote || !editable) return;
    setSaving(true);
    const body: any = {}; SAVE_FIELDS.forEach((k) => (body[k] = (quote as any)[k]));
    try { const { data } = await api.put(`/quotes/${quote.id}`, body); setQuote(data); toast.success("Saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  }, [quote, editable]);

  const ensureSaved = useCallback(async () => {
    if (!dirty || !quote) return true;
    try { const body: any = {}; SAVE_FIELDS.forEach((k) => (body[k] = (quote as any)[k])); const { data } = await api.put(`/quotes/${quote.id}`, body); setQuote(data); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  }, [dirty, quote]);

  const action = async (name: string, note = "", extra: any = {}) => {
    if (!quote) return;
    if (editable && dirty && !(await ensureSaved())) return;
    try {
      const shareUrlTemplate = `${window.location.origin}/q/{token}`;
      const { data } = await api.post(`/quotes/${quote.id}/actions/${name}`, { note, share_url: name === "send" ? shareUrlTemplate : undefined, ...extra });
      setQuote(data);
      const msgs: Record<string, string> = { approve: "Quote approved and snapshot saved", revise: `Revision v${data.current_version} created; previous version preserved`, submit_review: "Submitted for approval" };
      if (name === "send") {
        if (data.share_token) navigator.clipboard?.writeText(`${window.location.origin}/q/${data.share_token}`).catch(() => {});
        toast.success("Quote sent — share link copied");
        reportDelivery(data.delivery);
      } else toast.success(msgs[name] || "Done");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const [pane, setPane] = useState<"pages" | "preview" | "edit">("preview");
  const selectedPage = useMemo(() => quote?.pages.find((p) => p.id === selected), [quote, selected]);
  const selectedAtt = useMemo(() => selected.startsWith("att:") ? quote?.attachments.find((a) => a.id === selected.slice(4)) : null, [quote, selected]);

  if (!quote) return <div className="flex h-screen items-center justify-center text-sm text-gray-500" data-testid="builder-loading">Loading quote…</div>;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-100">
      <StatusBar quote={quote} dirty={dirty} saving={saving} onSave={save} onAction={action} editable={editable} />
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[260px_1fr_380px]">
        <div className={`${pane === "pages" ? "block" : "hidden"} min-h-0 lg:block`}>
        <LeftPanel quote={quote} selected={selected} onSelect={(s) => { setSelected(s); setPane("preview"); if (s.startsWith("att:")) setTab("files"); else if (tab === "files") setTab("content"); }} editable={editable} sections={sections}
          onPagesChange={(pages: Page[]) => { update({ pages }); }} onAttachmentsChange={(attachments) => update({ attachments })} />
        </div>

        <main className={`${pane === "preview" ? "block" : "hidden"} scrollbar-thin overflow-y-auto p-3 pb-20 sm:p-6 lg:block lg:p-8 lg:pb-8`} data-testid="canvas">
          <div className="mx-auto max-w-[760px]">
            {selectedAtt ? (
              <div className="overflow-hidden rounded-md bg-white shadow-[0_10px_40px_rgba(0,0,0,0.18)] ring-1 ring-black/5" data-testid="attachment-preview">
                <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2 text-xs text-gray-600"><span><b>{selectedAtt.title}</b> · {selectedAtt.mode === "section" ? "Inline section" : "Appendix"}</span><span>{selectedAtt.original_filename}</span></div>
                <div className="bg-gray-100 p-4"><PdfPages fileUrl={selectedAtt.url} title={selectedAtt.title} testid="attachment-pdf-pages" /></div>
              </div>
            ) : selectedPage ? (
              <div key={selectedPage.id} className="fade-up"><ProposalPage page={selectedPage} quote={quote} variant="paper" /></div>
            ) : <div className="text-center text-sm text-gray-500">Select a page</div>}
            <div className="mt-4 text-center text-xs text-gray-400">{selectedAtt ? `Imported PDF · ${selectedAtt.mode === "section" ? "inline section" : "appendix"}` : `Page ${quote.pages.findIndex((p) => p.id === selected) + 1} of ${quote.pages.length}`} · PDF and web proposal render from this same content</div>
          </div>
        </main>

        <aside className={`${pane === "edit" ? "flex" : "hidden"} min-h-0 flex-col border-l border-gray-200 bg-white pb-16 lg:flex lg:pb-0`} data-testid="right-panel">
          <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
            <TabsList className="grid h-auto w-full shrink-0 grid-cols-5 rounded-none border-b border-gray-200 bg-white p-0">
              {[["content", FileText], ["pricing", Receipt], ["images", ImageIcon], ["files", Paperclip], ["history", History]].map(([t, Icon]: any) => <TabsTrigger key={t} value={t} data-testid={`tab-${t}`} className="flex-col gap-0.5 rounded-none border-b-2 border-transparent py-2 text-[11px] capitalize data-[state=active]:border-blue-700 data-[state=active]:text-blue-700 data-[state=active]:shadow-none"><Icon size={14} />{t}</TabsTrigger>)}
            </TabsList>
            <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto p-4">
              <TabsContent value="content">
                {selectedPage ? <ContentTab page={selectedPage} editable={editable} sections={sections} onGoToTab={setTab}
                  onChange={(p) => update({ pages: quote.pages.map((x) => x.id === p.id ? p : x) })}
                  onApplyStyleToAll={(style) => update({ pages: quote.pages.map((x) => ({ ...x, style: { ...style } })) })}
                  onDelete={() => { const pages = quote.pages.filter((x) => x.id !== selectedPage.id); update({ pages }); setSelected(pages[0]?.id || ""); }} />
                  : <p className="text-xs text-gray-500">Select a page on the left, or open the Files tab for imported PDFs.</p>}
              </TabsContent>
              <TabsContent value="pricing"><PricingTab quote={quote} update={update} catalog={catalog} discountCatalog={discountCatalog} editable={editable} /></TabsContent>
              <TabsContent value="images"><ImagesTab quote={quote} update={update} setQuote={setQuote} editable={editable} ensureSaved={ensureSaved} /></TabsContent>
              <TabsContent value="files"><FilesTab quote={quote} update={update} appendixTypes={appendixTypes} editable={editable} onSelect={setSelected} /></TabsContent>
              <TabsContent value="history"><HistoryTab quote={quote} setQuote={setQuote} /></TabsContent>
            </div>
          </Tabs>
        </aside>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-3 border-t border-gray-200 bg-white lg:hidden" data-testid="builder-mobile-panes">
        {([["pages", "Pages", Layers], ["preview", "Preview", Eye], ["edit", "Edit", PenLine]] as const).map(([k, l, Icon]) => <button key={k} type="button" data-testid={`builder-pane-${k}`} onClick={() => setPane(k)} className={`flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium transition-colors ${pane === k ? "text-blue-700" : "text-gray-500"}`}><Icon size={17} />{l}</button>)}
      </nav>
    </div>
  );
}
